import { useEffect, useRef, useState } from 'react'
import { PexIcon } from './icons/PexIcon'
import { useT } from '../i18n'
import { employeeManagementEmbedI18n } from '../i18n/components/employee-management-embed'

// Cattr-specific wrapper around the remote.pexify.net iframe.
// Besides the embedded view, this surface also exposes a
// download dropdown for the desktop tracking agent. Installers are mirrored on
// our VPS under https://remote.pexify.net/downloads/cattr/ so the company is
// not exposed to GitHub release churn (Cattr could pull the artifacts at any
// time) and onboarding uses pexify.net brand URLs end-to-end.
type InstallerOption = {
  id:
    | 'windowsInstaller'
    | 'windowsPortable'
    | 'macIntelSilicon'
    | 'linuxAppImage'
    | 'linuxDebUbuntu'
    | 'linuxTarball'
  filename: string
  recommended?: boolean
}

const CATTR_SRC = 'https://remote.pexify.net/'
const DOWNLOAD_BASE = 'https://remote.pexify.net/downloads/cattr/'

const INSTALLERS: InstallerOption[] = [
  { id: 'windowsInstaller', filename: 'Cattr_Setup.exe', recommended: true },
  { id: 'windowsPortable', filename: 'Cattr.exe' },
  { id: 'macIntelSilicon', filename: 'Cattr.dmg' },
  { id: 'linuxAppImage', filename: 'Cattr.AppImage' },
  { id: 'linuxDebUbuntu', filename: 'Cattr.deb' },
  { id: 'linuxTarball', filename: 'Cattr.tar.gz' },
]

export function EmployeeManagementEmbed() {
  const t = useT(employeeManagementEmbedI18n)
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const onClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [open])

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-slate-200 appearance-dark:border-[var(--color-border)] bg-white appearance-dark:bg-[var(--color-surface)] px-4 py-2 text-sm">
        <div className="flex items-center gap-2 text-slate-700 appearance-dark:text-[var(--color-text)]">
          <span className="font-medium">{t.title}</span>
          <span className="text-xs text-slate-400 appearance-dark:text-[var(--color-text-muted)]">{t.embeddedView}</span>
        </div>
        <div className="flex items-center gap-2">
          <div ref={dropdownRef} className="relative">
            <button
              type="button"
              onClick={() => setOpen((prev) => !prev)}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 appearance-dark:border-[var(--color-border)] px-2 py-1 text-xs text-slate-600 appearance-dark:text-[var(--color-text-muted)] hover:bg-slate-50 appearance-dark:hover:bg-[var(--color-surface-muted)]"
            >
              <PexIcon name="download" className="h-3 w-3" size="xs" tone="inherit" />
              <span>{t.downloadDesktopApp}</span>
              <PexIcon name="caretDown" className="h-3 w-3" size="xs" tone="inherit" />
            </button>
            {open && (
              <ul className="absolute right-0 z-10 mt-1 w-72 rounded-md border border-slate-200 appearance-dark:border-[var(--color-border)] bg-white appearance-dark:bg-[var(--color-surface)] py-1 text-xs shadow-lg">
                {INSTALLERS.map((opt) => (
                  <li key={opt.filename}>
                    <a
                      href={`${DOWNLOAD_BASE}${opt.filename}`}
                      onClick={() => setOpen(false)}
                      className="flex items-center justify-between gap-2 px-3 py-1.5 text-slate-700 appearance-dark:text-[var(--color-text)] hover:bg-slate-50 appearance-dark:hover:bg-[var(--color-surface-muted)]"
                    >
                      <span>{t.installers[opt.id]}</span>
                      {opt.recommended && (
                        <span className="text-[10px] uppercase tracking-wider text-emerald-600 appearance-dark:text-[var(--color-success)]">
                          {t.recommended}
                        </span>
                      )}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <a
            href={CATTR_SRC}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded-md border border-slate-200 appearance-dark:border-[var(--color-border)] px-2 py-1 text-xs text-slate-600 appearance-dark:text-[var(--color-text-muted)] hover:bg-slate-50 appearance-dark:hover:bg-[var(--color-surface-muted)]"
          >
            <span>{t.openNewTab}</span>
            <PexIcon name="externalLink" className="h-3 w-3" size="xs" tone="inherit" />
          </a>
        </div>
      </div>
      <iframe
        title={t.title}
        src={CATTR_SRC}
        className="min-h-0 flex-1 w-full border-0 bg-white appearance-dark:bg-[var(--color-surface)]"
      />
    </div>
  )
}
